/**
 * Vuex Composition Helpers for Vue 3
 *
 * These helpers provide a similar API to vuex-composition-helpers
 * but work with Vuex 4 and Vue 3.
 */
import { computed, ComputedRef } from 'vue';
import { useStore as vuexUseStore, Store } from 'vuex';

// Re-export useStore from vuex
export function useStore(): Store<any> {
  return vuexUseStore();
}

/**
 * Map state properties to computed refs
 * @param namespace - Optional Vuex module namespace
 * @param keys - Array of state keys to map
 */
export function useState<T extends string>(keys: T[]): { [K in T]: ComputedRef<any> };
export function useState<T extends string>(
  namespace: string,
  keys: T[]
): { [K in T]: ComputedRef<any> };
export function useState<T extends string>(
  namespaceOrKeys: string | T[],
  maybeKeys?: T[]
): { [K in T]: ComputedRef<any> } {
  const store = vuexUseStore();

  let namespace: string | null = null;
  let keys: T[];

  if (Array.isArray(namespaceOrKeys)) {
    keys = namespaceOrKeys;
  } else {
    namespace = namespaceOrKeys;
    keys = maybeKeys!;
  }

  const result = {} as { [K in T]: ComputedRef<any> };

  for (const key of keys) {
    if (namespace) {
      result[key] = computed(() => store.state[namespace]?.[key]);
    } else {
      result[key] = computed(() => store.state[key]);
    }
  }

  return result;
}

/**
 * Map getters to computed refs
 * @param namespace - Optional Vuex module namespace
 * @param keys - Array of getter keys to map
 */
export function useGetters<T extends string>(keys: T[]): { [K in T]: ComputedRef<any> };
export function useGetters<T extends string>(
  namespace: string,
  keys: T[]
): { [K in T]: ComputedRef<any> };
export function useGetters<T extends string>(
  namespaceOrKeys: string | T[],
  maybeKeys?: T[]
): { [K in T]: ComputedRef<any> } {
  const store = vuexUseStore();

  let namespace: string | null = null;
  let keys: T[];

  if (Array.isArray(namespaceOrKeys)) {
    keys = namespaceOrKeys;
  } else {
    namespace = namespaceOrKeys;
    keys = maybeKeys!;
  }

  const result = {} as { [K in T]: ComputedRef<any> };

  for (const key of keys) {
    const getterKey = namespace ? `${namespace}/${key}` : key;
    result[key] = computed(() => store.getters[getterKey]);
  }

  return result;
}

/**
 * Map actions to callable functions
 * @param namespace - Optional Vuex module namespace
 * @param keys - Array of action keys to map
 */
export function useActions<T extends string>(
  keys: T[]
): { [K in T]: (payload?: any) => Promise<any> };
export function useActions<T extends string>(
  namespace: string,
  keys: T[]
): { [K in T]: (payload?: any) => Promise<any> };
export function useActions<T extends string>(
  namespaceOrKeys: string | T[],
  maybeKeys?: T[]
): { [K in T]: (payload?: any) => Promise<any> } {
  const store = vuexUseStore();

  let namespace: string | null = null;
  let keys: T[];

  if (Array.isArray(namespaceOrKeys)) {
    keys = namespaceOrKeys;
  } else {
    namespace = namespaceOrKeys;
    keys = maybeKeys!;
  }

  const result = {} as { [K in T]: (payload?: any) => Promise<any> };

  for (const key of keys) {
    const actionKey = namespace ? `${namespace}/${key}` : key;
    result[key] = (payload?: any) => store.dispatch(actionKey, payload);
  }

  return result;
}

/**
 * Map mutations to callable functions
 * @param namespace - Optional Vuex module namespace
 * @param keys - Array of mutation keys to map
 */
export function useMutations<T extends string>(keys: T[]): { [K in T]: (payload?: any) => void };
export function useMutations<T extends string>(
  namespace: string,
  keys: T[]
): { [K in T]: (payload?: any) => void };
export function useMutations<T extends string>(
  namespaceOrKeys: string | T[],
  maybeKeys?: T[]
): { [K in T]: (payload?: any) => void } {
  const store = vuexUseStore();

  let namespace: string | null = null;
  let keys: T[];

  if (Array.isArray(namespaceOrKeys)) {
    keys = namespaceOrKeys;
  } else {
    namespace = namespaceOrKeys;
    keys = maybeKeys!;
  }

  const result = {} as { [K in T]: (payload?: any) => void };

  for (const key of keys) {
    const mutationKey = namespace ? `${namespace}/${key}` : key;
    result[key] = (payload?: any) => store.commit(mutationKey, payload);
  }

  return result;
}
